import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ThemeToggle,
} from "@payflow/ui";
import { useAuth } from "../contexts/AuthContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { isPasskeySupported } from "../services/webauthn.service";

/*
 * Fluxo:
 *   1. "email"    — user digita email, clica Continuar
 *   2. "prompt"   — dispara passkey via OS
 *   3. "password" — fallback quando: (a) usuario nunca cadastrou passkey
 *      (bootstrap), OU (b) clicou em "Entrar com senha" pra pular a
 *      cerimonia. Apos login por senha, o modal <PasskeyBootstrapPrompt>
 *      no DashboardLayout forca cadastro se needsBootstrap=true.
 */
type LoginStep = "email" | "prompt" | "password";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithPasskey, login, isAuthenticated, isLoading: authLoading } = useAuth();
  const { t } = useTranslation();

  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const supported = isPasskeySupported();

  // Redireciona se ja estiver autenticado
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const from =
        (location.state as { from?: { pathname: string } })?.from?.pathname ||
        "/dashboard";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  const runPasskeyLogin = async (targetEmail?: string) => {
    setLoading(true);
    setError("");
    try {
      await loginWithPasskey(targetEmail);
      // AuthProvider triggers redirect via useEffect above.
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      /*
       * Se o backend disse que o usuario nao tem passkey ainda, cai
       * automaticamente pro modo senha — nao adianta ficar insistindo em
       * passkey (browser mostra QR code que nao vai resolver).
       */
      if (message.toLowerCase().includes("chave de acesso")) {
        setStep("password");
        setError("");
      } else {
        setStep("email");
        setError(mapPasskeyError(message, t));
      }
    } finally {
      setLoading(false);
    }
  };

  const runPasswordLogin = async () => {
    if (!email.trim() || !password) return;
    setLoading(true);
    setError("");
    try {
      await login(email.trim(), password);
      // AuthProvider triggers redirect; PasskeyBootstrapPrompt no DashboardLayout
      // vai forcar cadastro de passkey imediatamente.
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("errors.INVALID_CREDENTIALS"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStep("prompt");
    // Chama imediatamente — o prompt do OS abre no gesto do submit,
    // preservando o "user activation" que os browsers exigem.
    void runPasskeyLogin(email.trim());
  };

  const handleRetry = () => {
    void runPasskeyLogin(email.trim() || undefined);
  };

  // Mostra loading enquanto verifica autenticacao inicial
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <LanguageSelector />
        <ThemeToggle />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-primary">
            <span className="text-3xl font-bold text-white">P</span>
          </div>
          <CardTitle className="text-2xl">{t("auth.login_title")}</CardTitle>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("auth.passkey.login_subtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {!supported ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
                <p className="font-semibold text-warning">
                  {t("auth.passkey.unsupported_title")}
                </p>
                <p className="mt-1 text-muted-foreground">
                  {t("auth.passkey.unsupported_message")}
                </p>
              </div>
            </div>
          ) : step === "email" ? (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <Input
                label={t("auth.email")}
                name="email"
                type="email"
                autoComplete="username webauthn"
                placeholder={t("auth.email_placeholder")}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
                required
              />

              {error && <p className="text-sm text-error">{error}</p>}

              <Button type="submit" className="w-full" loading={loading}>
                {t("auth.passkey.continue")}
              </Button>

              <p className="pt-2 text-center text-xs text-muted-foreground">
                {t("auth.passkey.helper")}
              </p>

              <button
                type="button"
                onClick={() => {
                  if (!email.trim()) return;
                  setStep("password");
                  setError("");
                }}
                className="mx-auto mt-2 block text-xs text-primary-600 hover:underline"
              >
                Ainda nao tenho chave — entrar com senha
              </button>
            </form>
          ) : step === "password" ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void runPasswordLogin();
              }}
              className="space-y-4"
            >
              <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs text-muted-foreground">
                Modo temporario. Apos entrar, voce cadastra sua chave de acesso e nao
                precisa mais de senha.
              </div>

              <Input
                label={t("auth.email")}
                name="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <Input
                label={t("auth.password")}
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                required
              />

              {error && <p className="text-sm text-error">{error}</p>}

              <Button type="submit" className="w-full" loading={loading}>
                Entrar
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setStep("email");
                  setPassword("");
                  setError("");
                }}
              >
                Voltar
              </Button>
            </form>
          ) : (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                <FingerprintIcon />
              </div>
              <div>
                <p className="text-base font-semibold text-foreground">
                  {t("auth.passkey.authenticating_title")}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("auth.passkey.authenticating_message")}
                </p>
              </div>
              {loading ? (
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
              ) : (
                <div className="flex flex-col gap-2">
                  <Button className="w-full" onClick={handleRetry}>
                    {t("auth.passkey.retry")}
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setStep("email");
                      setError("");
                    }}
                  >
                    {t("common.back")}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <Link to="/" className="text-primary-600 hover:underline">
              {t("auth.back_home")}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function mapPasskeyError(
  code: string,
  t: (key: string) => string
): string {
  switch (code) {
    case "PASSKEY_CANCELED":
      return t("auth.passkey.error_canceled");
    case "PASSKEY_UNSUPPORTED":
      return t("auth.passkey.error_unsupported");
    case "PASSKEY_ALREADY_REGISTERED":
      return t("auth.passkey.error_already_registered");
    // Mensagens vindas do backend chegam como texto ja legivel
    default:
      return code || t("auth.passkey.error_generic");
  }
}

function FingerprintIcon() {
  return (
    <svg
      className="h-8 w-8"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.864 4.243A7.5 7.5 0 0119.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 004.5 10.5a7.464 7.464 0 01-1.15 3.993m1.989 3.559A11.209 11.209 0 008.25 10.5a3.75 3.75 0 117.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 01-3.6 9.75m6.633-4.596a18.666 18.666 0 01-2.485 5.33"
      />
    </svg>
  );
}
